import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBooks,
  getBook,
  searchBooks,
  semanticSearchBooks,
  updateBook,
  deleteBook,
  type GetBooksParams,
  type SearchBooksParams,
  type UpdateBookParams,
  type Book,
} from '../api/books';

export const bookKeys = {
  all: ['books'] as const,
  lists: () => [...bookKeys.all, 'list'] as const,
  list: (params?: GetBooksParams) => [...bookKeys.lists(), params] as const,
  search: (params: SearchBooksParams) => [...bookKeys.all, 'search', params] as const,
  semanticSearch: (params: SearchBooksParams) => [...bookKeys.all, 'semantic-search', params] as const,
  details: () => [...bookKeys.all, 'detail'] as const,
  detail: (id: number) => [...bookKeys.details(), id] as const,
};

export function useBooks(params?: GetBooksParams) {
  return useQuery({
    queryKey: bookKeys.list(params),
    queryFn: () => getBooks(params),
  });
}

export function useBook(id: number) {
  return useQuery({
    queryKey: bookKeys.detail(id),
    queryFn: () => getBook(id),
    enabled: id > 0,
  });
}

export function useSearchBooks(params: SearchBooksParams) {
  return useQuery({
    queryKey: bookKeys.search(params),
    queryFn: () => searchBooks(params),
    enabled: params.q.length > 0,
  });
}

export function useSemanticSearchBooks(params: SearchBooksParams) {
  return useQuery({
    queryKey: bookKeys.semanticSearch(params),
    queryFn: () => semanticSearchBooks(params),
    enabled: params.q.length > 0,
  });
}

export function useUpdateBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateBookParams }) =>
      updateBook(id, data),

    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: bookKeys.detail(id) });
      const previousBook = queryClient.getQueryData<Book>(bookKeys.detail(id));

      if (previousBook) {
        queryClient.setQueryData<Book>(bookKeys.detail(id), {
          ...previousBook,
          ...data,
        });
      }

      return { previousBook };
    },

    onError: (_err, { id }, context) => {
      if (context?.previousBook) {
        queryClient.setQueryData(bookKeys.detail(id), context.previousBook);
      }
    },

    onSettled: (_data, _error, { id }) => {
      queryClient.invalidateQueries({ queryKey: bookKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: bookKeys.lists() });
    },
  });
}

export function useDeleteBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteBook(id),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: bookKeys.all });
    },
  });
}
